import { Container } from '@acies/core';
import Label from './label';
import theme from './theme';

export default class LabelValueUnit extends Container {
	/** @param {string} value What the value is called. */
	public set text(value: string) {
		if (this.#text === value) {
			return;
		}
		assertIsString(value);
		this.#text = value;
		this.#textChanged = true;
		this.invalidateProperties();
	}

	public get text(): string {
		return this.#text;
	}

	/** @param {number | string} value The value itself, which is read as a measurement when it is a number and as words when it is a string. */
	public set value(value: number | string) {
		if (this.#value === value) {
			return;
		}
		assertIsNumberOrString(value);
		this.#value = value;
		this.#valueChanged = true;
		this.invalidateProperties();
	}

	public get value(): number | string {
		return this.#value;
	}

	/** @param {string} value What the value is measured in. */
	public set unit(value: string) {
		if (this.#unit === value) {
			return;
		}
		assertIsString(value);
		this.#unit = value;
		this.#unitChanged = true;
		this.invalidateProperties();
	}

	public get unit(): string {
		return this.#unit;
	}

	#text: string;
	#textChanged: boolean;
	#value: number | string;
	#valueChanged: boolean;
	#unit: string;
	#unitChanged: boolean;
	#textLabel!: Label;
	#valueUnitContainer!: Container;
	#valueContainer!: Container;
	#valueLabel!: Label;
	#unitContainer!: Container;
	#unitLabel!: Label;
	#numberFormatter: Intl.NumberFormat;

	/** Two decimals whatever the number is, and a grouped thousands separator, so a column of numbers lines up and reads at a glance. */
	get #numberFormatOptions(): Intl.NumberFormatOptions {
		return {
			maximumFractionDigits: 2,
			minimumFractionDigits: 2,
			useGrouping: true,
		};
	}

	public constructor() {
		super();
		// 'da' is what puts the point between thousands and the comma before the decimals
		this.#numberFormatter = new Intl.NumberFormat('da', this.#numberFormatOptions);
		this.#text = 'Label';
		this.#textChanged = false;
		this.#value = 'Value';
		// the value is written by #commitValue alone, so the label never holds an unformatted number, not even the first one
		this.#valueChanged = true;
		this.#unit = 'UN';
		this.#unitChanged = false;
		this.minWidth = 128;
		this.autoLayout = 'vertical';
		this.gapVertical = 4;
	}

	/** What the value is called. */
	public get textLabel(): Label {
		if (!this.#textLabel) {
			this.#textLabel = new Label();
			this.#textLabel.width = 'fill';
			this.#textLabel.size = 'medium';
			this.#textLabel.text = this.text;
			this.#textLabel.variant = 'light';
			this.#textLabel.lineHeight = 15;
		}
		return this.#textLabel;
	}

	/** The value and its unit, side by side as one control. */
	public get valueUnitContainer(): Container {
		if (!this.#valueUnitContainer) {
			this.#valueUnitContainer = new Container();
			this.#valueUnitContainer.width = 'fill';
			this.#valueUnitContainer.height = 32;
			this.#valueUnitContainer.addComponents([this.valueContainer, this.unitContainer]);
		}
		return this.#valueUnitContainer;
	}

	public get valueContainer(): Container {
		if (!this.#valueContainer) {
			this.#valueContainer = new Container();
			this.#valueContainer.width = 'fill';
			this.#valueContainer.height = 'fill';
			// the two are rounded on their outer corners only, so value and unit read as one control
			this.#valueContainer.cornerRadius = [4, 0, 0, 4];
			this.#valueContainer.stroke = theme.strokes.soft;
			this.#valueContainer.paddingHorizontal = 10;
			this.#valueContainer.fill = theme.colors.fill.white;
			this.#valueContainer.alignment = 'left';
			this.#valueContainer.addComponents([this.valueLabel]);
		}
		return this.#valueContainer;
	}

	/** The value itself. */
	public get valueLabel(): Label {
		if (!this.#valueLabel) {
			this.#valueLabel = new Label();
			this.#valueLabel.width = 'fill';
			this.#valueLabel.textAlignHorizontal = 'left';
		}
		return this.#valueLabel;
	}

	public get unitContainer(): Container {
		if (!this.#unitContainer) {
			this.#unitContainer = new Container();
			this.#unitContainer.minWidth = 32;
			this.#unitContainer.paddingHorizontal = 10;
			this.#unitContainer.height = 'fill';
			this.#unitContainer.cornerRadius = [0, 4, 4, 0];
			this.#unitContainer.stroke = theme.strokes.soft;
			this.#unitContainer.fill = theme.colors.fill.weak;
			this.#unitContainer.alignment = 'center';
			this.#unitContainer.addComponents([this.unitLabel]);
		}
		return this.#unitContainer;
	}

	/** What the value is measured in. */
	public get unitLabel(): Label {
		if (!this.#unitLabel) {
			this.#unitLabel = new Label();
			this.#unitLabel.text = this.unit;
		}
		return this.#unitLabel;
	}

	protected override initialize(): void {
		super.initialize();
		this.addComponents([this.textLabel, this.valueUnitContainer]);
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#textChanged) {
			this.#commitText();
		}
		if (this.#valueChanged) {
			this.#commitValue();
		}
		if (this.#unitChanged) {
			this.#commitUnit();
		}
	}

	#commitUnit(): void {
		this.#unitChanged = false;
		this.unitLabel.text = this.unit;
	}

	#commitText(): void {
		this.#textChanged = false;
		this.textLabel.text = this.text;
	}

	/** A number is a measurement, which is read off against the ones above and below it, so it is set flush to the unit. Words are read from the left as any other text is. */
	#commitValue(): void {
		this.#valueChanged = false;
		if (typeof this.value === 'number') {
			this.valueLabel.text = this.#numberFormatter.format(this.value);
			this.valueLabel.textAlignHorizontal = 'right';
		} else {
			this.valueLabel.text = this.value;
			this.valueLabel.textAlignHorizontal = 'left';
		}
	}
}
customElements.define('ac-label-value-unit', LabelValueUnit);

function assertIsString(value: unknown): asserts value is string {
	if (typeof value === 'string') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type string`);
}

function assertIsNumberOrString(value: unknown): asserts value is number | string {
	if (typeof value === 'number' || typeof value === 'string') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type number or string`);
}
